import {For,Show,Match,Switch,createSignal, createMemo, ErrorBoundary, createResource, createEffect } from 'solid-js';
import {CardItem} from 'Shared/frontend/menuPage/CardItem';
import {ImagelessItem} from 'Shared/frontend/menuPage/ImagelessItem';
import {Overlay} from 'Shared/frontend/Overlay';
import {subdoc} from 'Common/ViewUtils';
import {jumpToAnchor, SubNav} from 'Shared/frontend/SubNav';
import {Location} from 'Common/Location';
import { SpecialsOverlayContent } from 'Shared/frontend/specialsPosters/SpecialsOverlayContent';
import { SpecialsPoster } from 'Shared/frontend/specialsPosters/SpecialsPoster';
import { css } from '@emotion/css';
import { theme, palette, sizes } from 'Shared/frontend/SettingsProvider';
import { capitalise } from 'Shared/common/Capitalise';
import { posterGridStyle } from 'Shared/frontend/posterGridStyle';
import { OverlayItem } from 'Shared/frontend/menuPage/OverlayItem';
import { fluidType } from 'Shared/common/FluidType';
import { useServer } from 'Shared/SiteProvider';
import { PageMeta } from 'Shared/frontend/PageMeta';
import { useLocation, useNavigate } from '@solidjs/router';
import { initPageDetails } from 'Shared/frontend/PageDetailsProvider';
import { Reveal } from 'Shared/frontend/Reveal';
import { IgnorePageMarginsStyle } from 'Shared/frontend/IgnorePageMarginsStyle';
import { PosterWrap } from 'Shared/frontend/PosterWrap';

const stickyStyle = () => css({
	position: 'sticky',
	top: '0',
	zIndex: '10',
});

//XXX it may make sense to separate the grid -- much of MenuPage? -- into separate Standard and Imageless files in time.

const imagelessGridStyle = () => css({});

const standardGridStyle = () => css({
	maxWidth: '100%',
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'center',
	gap: '1.5em',
	alignItems: 'stretch',

	['@media screen and (min-width: 745px)']: {
		margin: '0 20px',
	},

	['@media screen and (max-width: 745px)']: {
		margin: '40px auto'
	}
});

const linkWrapStyle = () => css(theme().a,{
	display: 'block',

	['@media screen and (max-width: 745px)']: {
		width: '100%',
	},

	/* The cards look a little wide on narrow desktop pages. Larger margins help a bit. */
//XXX NB only applies to menus with images
	['@media screen and (min-width:600px) and (max-width: 745px)']: {
		marginLeft: fluidType('px',500,745,14,38),
		marginRight: fluidType('px',500,745,14,38)
	}
});

const buttonRowStyle = () => css({
	display: 'flex',
	justifyContent: 'center'
});

//const buttonRowButtonStyle = () => css(theme().widgets.subNav.button, {
const buttonRowButtonStyle = () => css(theme().button, {
	textTransform:'capitalize'
});

const subNavButtonStyle = type => css(
	theme().subNav.button,
	theme().subNav.buttonType(type),
	{
		[`@media (max-width: ${sizes().flipToMobileNav}px)`]: {
			paddingLeft: '1em',  //BEN had 20em but that looked wrong
			paddingRight: '1em', // BEN had 10em but that looked wrong
			marginLeft: '0.25em',
			marginRight: '0.25em',
		}
	}
);

export const specialItemOverlayStyle = () => css({
	display: 'block',
	maxWidth: '450px',
	minHeight: 'auto',
	backgroundColor: palette().background
});

const aStyle = () => css(theme().a, {
});

const detailsStyle = () => css(theme().text.plain, {
	textAlign: 'center',

	img: {
		maxWidth:'100%',
		height: 'auto',
	},
});


const pageName = 'frontend/Menu';


export default function MenuPage()
{
	initPageDetails(pageName,'menu');

	const slug = useLocation().pathname.slice(1);

	const [menu] = createResource(() => useServer().read.query({page:pageName,permission:'menu',params:{slug:slug}}));
	const [specials] = createResource(() => useServer().list.query({page:pageName,permission:'specials'}));

	return (
		<Reveal when={menu() && specials()}>
			<MenuPageContents menu={menu()} specials={specials()} />
		</Reveal>
	);
}


//TODO separate grid out into StandardMenu and ImagelessMenu	

function MenuPageContents(P)
{
	const [currentLocation,setCurrentLocation] = createSignal();
	const [currentSpecial,setCurrentSpecial] = createSignal();

	const overlayItem = () => subdoc(P.menu,currentLocation());
	const topSection = () => subdoc(P.menu,currentLocation().slice(0,2));

	const itemsExist = createMemo(() => P.menu.items?.find(i => i.status=='published'));

	const style = () => theme().item.type;

	const tit = () => P.menu?.title;

	return (<>
		<PageMeta title= {tit()}/>

		<div class='anchor' id='top' />

		<div>
{/* TODO compare this topContentStyle to hire page and home page styling. Remove it? */}
			<div class={css(theme().section)}> 
				<Show when={P.menu.teaser}>
					<div class={css(theme().text.teaser)}>{P.menu.teaser}</div>
				</Show>

				<Show when={P.menu.title && ! P.menu.hideTitle }>
					<h3 class={css(theme().text.pageTitle)}>{P.menu.title}</h3>
				</Show>

				<Show when={P.menu.tagline}>
					<div class={css(theme().text.tagline)}>{P.menu.tagline}</div>
				</Show>

				<Show when={P.menu.details}>
					<div class={css(theme().text.plain)} innerHTML={P.menu.details} />
				</Show>
			</div>

			<Show when={itemsExist()}>
				<div class={style()=='imageless' ? imagelessGridStyle() : standardGridStyle()}>
					<For each={P.menu.items}>{ (item,index) => 
						<Show when={item.status=='published'}>
							<MenuItem item={item} menu={P.menu} location={['items',index()]} setCurrentLocation={setCurrentLocation} />
						</Show>
					}</For>
				</div>
			</Show>

			<Show when={P.menu.sections && P.menu.sections.length > 0}>
				<For each={P.menu.sections}>{ (section,index) => 
					<DisplaySectionOrWidget level={2} 
						section={section} 
						menu={P.menu}
						location={['sections',index()]}
						setCurrentLocation={setCurrentLocation} 
						specials={P.specials}
						setCurrentSpecial={setCurrentSpecial} 
					/>
				}</For>
			</Show>
		</div>

		{/* Overlays: */}

		<Show when={currentLocation()}>
			<Overlay iconOverImage={false} animation='slideIn' close={() => setCurrentLocation(undefined)}>
				<OverlayItem
					menu={P.menu}
					location={currentLocation()}
					item={overlayItem()}
					section={topSection()}
				/>
			</Overlay>
		</Show>

		{/* TODO add product type colouring/styling to special overlays? And maybe posters? */}
		{/* NOTE currentSpecial is a number and can be 0 */}
		<Show when={currentSpecial()!=null}>
			<Overlay iconOverImage={true} animation='slideIn' close={() => setCurrentSpecial(undefined)}>
				<ErrorBoundary fallback={(err) => <div>Problem showing item</div>}> 
					<div class={specialItemOverlayStyle()}>
						<SpecialsOverlayContent special={P.specials[currentSpecial()]} times={P.specials[currentSpecial()].times} />
					</div>
				</ErrorBoundary>  
			</Overlay>
		</Show>
	</>);
}

//TODO compare to MenuPageContents()
function Section(P)
{
	const style = () => theme().item.type;

	return (<>
		<div class='anchor' id={menuSelector(P.menu,P.location)} />

		<section class={css(theme().section)}>
			<Show when={P.section.teaser}>
				<div class={css(theme().text.teaser)}>{P.section.teaser}</div>
			</Show>

			<Show when={P.section.title}>
				<h3 class={css(theme().title(P.level,P.productType,P.section))}>{P.section.title}</h3>
			</Show>

			<Show when={P.section.tagline}>
				<div class={css(theme().text.tagline)}>{P.section.tagline}</div>
			</Show>

			<Show when={P.section.details}>
				<div class={detailsStyle()} innerHTML={P.section.details} />
			</Show>

			<div class={style()=='imageless' ? imagelessGridStyle() : standardGridStyle()}>
				<For each={P.section.items}>{ (item,index) => 
					<Show when={item.status=='published'}>
						<MenuItem {...P}
							location={[...P.location,'items',index()]} 
							setCurrentLocation={P.setCurrentLocation} 
							item={item} 
						/>
					</Show>
				}</For>
			</div>

			<Show when={P.section.sections && P.section.sections.length > 0}>
				<For each={P.section.sections}>{ (subsection,index) => 
					<DisplaySectionOrWidget {...P}
						level={P.level+1} 
						section={subsection} 
						location={[...P.location,'sections',index()]}
						setCurrentLocation={P.setCurrentLocation} 
						setCurrentSpecial={P.setCurrentSpecial} 
					/>
				}</For>
			</Show>
		</section>
	</>);
}

function DisplaySectionOrWidget(P)
{
	const productType = () => calculateProductType(P.menu,P.location);

	return (<>
		{P.section.widget ? 
			<Widget {...P} productType={productType()} widget={P.section} />  :  
			<Section {...P} productType={productType()} /> 
		}
	</>);
}

function MenuItem(P) 
{
	const style = () => theme().item.type;

	/* TODO We could be more efficient about calculating this... */
	const productType = () => calculateProductType(P.menu,P.location);
	const id = createMemo(() => menuSelector(P.menu,P.location));

	return (<>
		<Switch>
			<Match when={style()=='grid'}>
				<a class={linkWrapStyle()} href='' onclick={e => {e.preventDefault(); P.setCurrentLocation(P.location)}} >
					<div id={id()} class='anchor' />
					<CardItem {...P} id={id()} productType={productType()} />
				</a>
			</Match>
			<Match when={style()=='imageless'}>
				<div id={id()} class='anchor' />
				<ImagelessItem {...P} id={id()} productType={productType()} />
			</Match>
		</Switch>
	</>);
}

/* Used by the backend */
export function MenuItemPreview(P) 
{
	const style = () => theme().item.type;
	/* TODO We could be more efficient about calculating this... */
	const productType = () => calculateProductType(P.menu,P.location);

	return (<>
		<Switch>
			<Match when={style()=='grid'}>
				<CardItem {...P} id='unused' productType={productType()} />
			</Match>
			<Match when={style()=='imageless'}>
				<ImagelessItem {...P} id='unused' productType={productType()} />
			</Match>
		</Switch>
	</>);
}

function Widget(P)
{
	return (
		<Switch>
			<Match when={P.widget.widget=='buttonRow'}>
				<ButtonsRowWidget {...P} />
			</Match>
			<Match when={P.widget.widget=='specials'}>
				<SpecialsWidget {...P} />
			</Match>
		</Switch>
	);
}

function ButtonsRowWidget(P)
{
	const navigate = useNavigate();

//XXX cf 'open in new window' open? ALSO make consistent between the sticky version and the non-sticky

	return (
		<Switch>
			<Match when={P.widget.sticky}>
				<div class={stickyStyle()}>
					<IgnorePageMarginsStyle>
						<SubNav>
							<For each={P.widget.buttons}>{button =>
								<button class={subNavButtonStyle(button.style)} onClick={() => jumpToAnchor(navigate,button.url)}>{button.label}</button>
							}</For>
						</SubNav>
					</IgnorePageMarginsStyle>
				</div>
			</Match>
			<Match when={true}>
				<div class={buttonRowStyle()}>
					<For each={P.widget.buttons}>{button =>
						<a href={button.url}>
							<button class={buttonRowButtonStyle()}>{button.label}</button>
						</a>
					}</For>
				</div>
			</Match>
		</Switch>
	);
}


function SpecialsWidget(P)
{
//TODO insert title standard way + taglines etc

	return (
		<Show when={P.specials?.length > 0}>
			<div id='specials' />

			<section>
				<h3 class={css(theme().title(P.level,P.productType,P.section))}>Specials</h3>

				<div class={posterGridStyle()}>
					<For each={P.specials}>{(special,index) => 
						<a class={aStyle()} href='' onclick={() => P.setCurrentSpecial(index)} >
							<ErrorBoundary fallback={(err) => <div>Problem showing item</div>}>  
								<PosterWrap settings={special.posterSettings}>
									<SpecialsPoster {...special} location={[index]} />
								</PosterWrap>
							</ErrorBoundary>	
						</a>
					}</For>
				</div>
			</section>
		</Show>
	);
}

//TODO share with backend??
function calculateProductType(menu,location:Location)
{
	if (menu.productType!=undefined && menu.productType!='mixed')
		return menu.productType;

	let data = menu;
	for (let i=0; i<location.length; i++) {
		data = data[location[i]];

		if (data.productType!=undefined && data.productType!='mixed')
			return data.productType;
	}

	return 'mixed';
}


/* Selectors used for sections, subsections and menu items. */
export function menuSelector(menu,location:Location)
{
	let subdoc = menu;
	let useSection = false;
	let useItems = false;
	let id = '';
	let sep = '';

	let i = 0;
	for (const e of location) {
		subdoc = subdoc[e];

		if (useSection) {
			id += sep + capitalise(subdoc.title ?? ''+i).replace(/\W/g,'');
			sep = '.';
		}
		if (useItems) {
			id += sep + capitalise(subdoc.name ?? ''+i).replace(/\W/g,'');
			sep = '.';
		}

		useSection = e=='sections';
		useItems = e=='items' ;
		i++;
	}
	return id;
}

